import { useMemo } from 'react';
import {
    Package,
    TrendingUp,
    BarChart3,
    PieChart as PieIcon,
    Scale,
    Layers,
    IndianRupee,
    Weight,
    XCircle,
    AlertTriangle,
} from 'lucide-react';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    LineChart,
    Line,
    PieChart,
    Pie,
    Cell,
} from 'recharts';

import KPICard from '../components/common/KPICard';
import ChartCard from '../components/common/ChartCard';
import DataTable from '../components/common/DataTable';
import { useFilters } from '../context/FilterContext';
import { applyMultiLevelFilters } from '../utils/filters';
import { formatCurrency, formatNumber, formatPercentage, formatDate } from '../utils/formatters';
import { aggregateByTimePeriod } from '../utils/calculations';
import styles from './OrderAnalytics.module.css';

// Known CAT-ID → color mapping
const CAT_COLORS = {
    'CAT-RAW': '#E53935',
    'CAT-RTC-NV': '#FB8C00',
    'CAT-RTC-V': '#43A047',
    'CAT-EGG': '#FDD835',
    'CAT-OIL': '#FFB300',
    'CAT-MSL': '#8E24AA',
    'CAT-MUTTON': '#6D4C41',
    'CAT-OTH': '#607D8B',
};
const PALETTE = ['#E53935', '#FB8C00', '#43A047', '#FDD835', '#FFB300', '#8E24AA', '#6D4C41', '#607D8B', '#1E88E5', '#00ACC1'];
function getColor(catId, index) { return CAT_COLORS[catId] || PALETTE[index % PALETTE.length]; }

// ────────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────────
function CategoryPerformance() {
    const { appliedFilters, orders } = useFilters();

    const filteredOrders = useMemo(() => {
        const filters = {
            startDate: appliedFilters.dateRange?.startDate
                ? appliedFilters.dateRange.startDate.toISOString().split('T')[0] : null,
            endDate: appliedFilters.dateRange?.endDate
                ? appliedFilters.dateRange.endDate.toISOString().split('T')[0] : null,
            cities: appliedFilters.cities,
            stores: appliedFilters.stores,
            pincodes: appliedFilters.pincodes,
        };
        return applyMultiLevelFilters(orders, filters);
    }, [appliedFilters, orders]);

    // ── Split into active vs cancelled ──────────────────────────
    const activeOrders = useMemo(() => filteredOrders.filter(o => o.status !== 'Cancelled'), [filteredOrders]);
    const cancelledOrders = useMemo(() => filteredOrders.filter(o => o.status === 'Cancelled'), [filteredOrders]);

    // ── Category data (active orders only for revenue/volume) ──
    const categoryData = useMemo(() => {
        const catMap = {};

        activeOrders.forEach(order => {
            order.items.forEach(item => {
                const key = item.categoryId || 'CAT-OTH';
                if (!catMap[key]) {
                    catMap[key] = { id: key, name: item.categoryName || key, revenue: 0, volumeKg: 0, qty: 0, orderSet: new Set() };
                }
                catMap[key].revenue += item.total || 0;
                catMap[key].volumeKg += (item.unitQuantity || 0) * (item.quantity || 1);
                catMap[key].qty += item.quantity || 1;
                catMap[key].orderSet.add(order.id);
            });
        });

        const totalRevenue = Object.values(catMap).reduce((s, c) => s + c.revenue, 0);
        const totalQty = Object.values(catMap).reduce((s, c) => s + c.qty, 0);

        return Object.values(catMap)
            .map((cat, i) => ({
                ...cat,
                orderCount: cat.orderSet.size,
                color: getColor(cat.id, i),
                revenuePercent: totalRevenue > 0 ? (cat.revenue / totalRevenue) * 100 : 0,
                qtyPercent: totalQty > 0 ? (cat.qty / totalQty) * 100 : 0,
                avgPricePerUnit: cat.qty > 0 ? cat.revenue / cat.qty : 0,
                avgRatePerKg: cat.volumeKg > 0 ? cat.revenue / cat.volumeKg : 0,
            }))
            .sort((a, b) => b.revenue - a.revenue);
    }, [activeOrders]);

    // ── Cancellation data by category ──────────────────────────
    const cancellationData = useMemo(() => {
        // Map: categoryId → { cancelledOrderSet, totalOrderSet, lostRevenue, lostQty, lostVolumeKg }
        const cancelMap = {};
        const totalMap = {};  // total orders (all statuses) per category

        // Build total order sets from ALL filteredOrders
        filteredOrders.forEach(order => {
            order.items.forEach(item => {
                const key = item.categoryId || 'CAT-OTH';
                if (!totalMap[key]) totalMap[key] = { name: item.categoryName || key, ordSet: new Set() };
                totalMap[key].ordSet.add(order.id);
            });
        });

        // Build cancel stats from CANCELLED orders only
        cancelledOrders.forEach(order => {
            order.items.forEach(item => {
                const key = item.categoryId || 'CAT-OTH';
                if (!cancelMap[key]) {
                    cancelMap[key] = {
                        id: key,
                        name: item.categoryName || key,
                        cancelledOrders: new Set(),
                        lostRevenue: 0,
                        lostQty: 0,
                        lostVolumeKg: 0,
                    };
                }
                cancelMap[key].cancelledOrders.add(order.id);
                cancelMap[key].lostRevenue += item.total || 0;
                cancelMap[key].lostQty += item.quantity || 1;
                cancelMap[key].lostVolumeKg += (item.unitQuantity || 0) * (item.quantity || 1);
            });
        });

        return Object.values(cancelMap)
            .map((c, i) => {
                const totalCount = totalMap[c.id]?.ordSet.size || 0;
                const cancelCount = c.cancelledOrders.size;
                return {
                    id: c.id,
                    name: c.name,
                    color: getColor(c.id, i),
                    cancelledOrders: cancelCount,
                    totalOrders: totalCount,
                    cancellationRate: totalCount > 0 ? (cancelCount / totalCount) * 100 : 0,
                    lostRevenue: c.lostRevenue,
                    lostQty: c.lostQty,
                    lostVolumeKg: c.lostVolumeKg,
                };
            })
            .sort((a, b) => b.cancelledOrders - a.cancelledOrders);
    }, [filteredOrders, cancelledOrders]);

    // ── Totals for summary KPIs ────────────────────────────────
    const totalCancelledOrders = cancelledOrders.length;
    const totalLostRevenue = cancellationData.reduce((s, c) => s + c.lostRevenue, 0);
    const worstCategory = cancellationData[0] || null;

    // ── Revenue trend (active orders) ──────────────────────────
    const categoryTrendData = useMemo(() => {
        const daily = aggregateByTimePeriod(activeOrders, 'day');
        return daily.slice(-30).map(d => {
            const point = { date: formatDate(d.period, 'short') };
            categoryData.forEach(cat => {
                const catItems = d.items.flatMap(o => o.items || []).filter(i => i.categoryId === cat.id);
                point[cat.name] = catItems.reduce((s, i) => s + (i.total || 0), 0);
            });
            return point;
        });
    }, [activeOrders, categoryData]);

    // ── Per-Kg bar ──────────────────────────────────────────────
    const perKgData = useMemo(() =>
        categoryData.filter(c => c.volumeKg > 0)
            .map(c => ({ name: c.name, 'Avg Rate/Kg': Math.round(c.avgRatePerKg), color: c.color })),
        [categoryData]
    );

    const pieData = useMemo(() =>
        categoryData.map(c => ({ name: c.name, value: Math.round(c.revenue), color: c.color })),
        [categoryData]
    );

    // ── Table columns ───────────────────────────────────────────
    const tableColumns = [
        { key: 'name', label: 'Category' },
        { key: 'revenue', label: 'Revenue', render: v => formatCurrency(v, { compact: true }) },
        { key: 'qty', label: 'Qty (Units)', render: v => formatNumber(v) },
        { key: 'volumeKg', label: 'Volume (Kg)', render: v => formatNumber(v, { decimals: 2 }) },
        { key: 'orderCount', label: 'Orders' },
        { key: 'revenuePercent', label: '% Revenue', render: v => formatPercentage(v) },
        { key: 'qtyPercent', label: '% Qty Share', render: v => formatPercentage(v) },
        { key: 'avgPricePerUnit', label: 'Avg Price/Unit', render: v => formatCurrency(v) },
        { key: 'avgRatePerKg', label: 'Avg Rate/Kg', render: v => v > 0 ? `₹${v.toFixed(0)}/kg` : 'N/A' },
    ];

    const cancelColumns = [
        { key: 'name', label: 'Category' },
        { key: 'totalOrders', label: 'Total Orders' },
        { key: 'cancelledOrders', label: 'Cancelled Orders' },
        {
            key: 'cancellationRate', label: 'Cancel Rate %',
            render: v => (
                <span style={{
                    color: v > 20 ? 'var(--color-error)' : v > 10 ? 'var(--color-warning)' : 'var(--color-success)',
                    fontWeight: 600,
                }}>
                    {formatPercentage(v)}
                </span>
            )
        },
        { key: 'lostRevenue', label: 'Lost Revenue', render: v => formatCurrency(v, { compact: true }) },
        { key: 'lostQty', label: 'Lost Qty', render: v => formatNumber(v) },
        { key: 'lostVolumeKg', label: 'Lost Wt (Kg)', render: v => v > 0 ? formatNumber(v, { decimals: 2 }) : 'N/A' },
    ];

    const top4 = categoryData.slice(0, 4);
    const rest = categoryData.slice(4);

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <h1 className={styles.title}>Category Performance</h1>
                <p className={styles.subtitle}>Live category breakdown — revenue from active orders only, cancellations tracked separately</p>
            </header>

            {/* Top Category KPI Cards */}
            <div className={styles.kpiGrid4}>
                {top4.map(cat => (
                    <KPICard
                        key={cat.id}
                        title={cat.name}
                        value={formatCurrency(cat.revenue, { compact: true })}
                        icon={Package}
                        color={cat.id === 'CAT-RAW' ? 'danger' : cat.id === 'CAT-RTC-NV' ? 'warning' : cat.id === 'CAT-EGG' ? 'info' : 'success'}
                    />
                ))}
            </div>

            {rest.length > 0 && (
                <div className={styles.kpiGrid3} style={{ marginTop: 'var(--spacing-3)' }}>
                    {rest.map(cat => (
                        <KPICard
                            key={cat.id}
                            title={cat.name}
                            value={formatCurrency(cat.revenue, { compact: true })}
                            icon={Package}
                            color="secondary"
                            compact
                        />
                    ))}
                </div>
            )}

            {/* ════════════════════════════════════════════════════
                CANCELLATION BY CATEGORY
            ════════════════════════════════════════════════════ */}
            <section className={styles.section} style={{ marginTop: 'var(--spacing-5)' }}>
                <h2 className={styles.sectionTitle}>
                    <XCircle size={20} className={styles.sectionIcon} style={{ color: 'var(--color-error)' }} />
                    Cancellation by Category
                </h2>

                {/* Cancellation KPI Summary */}
                <div className={styles.kpiGrid3} style={{ marginBottom: 'var(--spacing-4)' }}>
                    <KPICard
                        title="Total Cancelled Orders"
                        value={formatNumber(totalCancelledOrders)}
                        icon={XCircle}
                        color="danger"
                    />
                    <KPICard
                        title="Total Lost Revenue"
                        value={formatCurrency(totalLostRevenue, { compact: true })}
                        icon={IndianRupee}
                        color="warning"
                    />
                    <KPICard
                        title="Worst Category"
                        value={worstCategory?.name || 'N/A'}
                        subtitle={worstCategory ? `${worstCategory.cancelledOrders} cancellations` : ''}
                        icon={AlertTriangle}
                        color="danger"
                    />
                </div>

                {/* Cancellation Chart — horizontal bar ordered by most cancellations */}
                {cancellationData.length > 0 && (
                    <div className={styles.chartsGrid} style={{ marginBottom: 'var(--spacing-4)' }}>
                        {/* Cancelled Orders Count per Category */}
                        <ChartCard title="Cancelled Orders by Category" icon={XCircle} height={300}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={cancellationData}
                                    layout="vertical"
                                    margin={{ left: 10, right: 20, top: 8, bottom: 8 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                                    <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                                    <YAxis
                                        dataKey="name"
                                        type="category"
                                        tick={{ fontSize: 10 }}
                                        width={90}
                                        tickFormatter={v => v.length > 12 ? v.slice(0, 12) + '…' : v}
                                    />
                                    <Tooltip
                                        contentStyle={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 8 }}
                                        formatter={(value, name, props) => {
                                            const d = props.payload;
                                            return [
                                                `${value} orders  (${d.cancellationRate?.toFixed(1)}% cancel rate)`,
                                                'Cancelled'
                                            ];
                                        }}
                                    />
                                    <Bar dataKey="cancelledOrders" name="Cancelled Orders" radius={[0, 4, 4, 0]}>
                                        {cancellationData.map((entry, i) => (
                                            <Cell key={i} fill={entry.color} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </ChartCard>

                        {/* Cancellation Rate % per Category */}
                        <ChartCard title="Cancellation Rate % by Category" icon={AlertTriangle} height={300}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={[...cancellationData].sort((a, b) => b.cancellationRate - a.cancellationRate)}
                                    layout="vertical"
                                    margin={{ left: 10, right: 20, top: 8, bottom: 8 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                                    <XAxis
                                        type="number"
                                        tick={{ fontSize: 10 }}
                                        tickFormatter={v => `${v.toFixed(0)}%`}
                                        domain={[0, 100]}
                                    />
                                    <YAxis
                                        dataKey="name"
                                        type="category"
                                        tick={{ fontSize: 10 }}
                                        width={90}
                                        tickFormatter={v => v.length > 12 ? v.slice(0, 12) + '…' : v}
                                    />
                                    <Tooltip
                                        contentStyle={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 8 }}
                                        formatter={(value, name, props) => {
                                            const d = props.payload;
                                            return [
                                                `${value.toFixed(1)}%  (${d.cancelledOrders} of ${d.totalOrders} orders)`,
                                                'Cancel Rate'
                                            ];
                                        }}
                                    />
                                    <Bar dataKey="cancellationRate" name="Cancel Rate %" radius={[0, 4, 4, 0]}>
                                        {[...cancellationData]
                                            .sort((a, b) => b.cancellationRate - a.cancellationRate)
                                            .map((entry, i) => (
                                                <Cell
                                                    key={i}
                                                    fill={entry.cancellationRate > 20 ? '#EF4444' : entry.cancellationRate > 10 ? '#F59E0B' : '#10B981'}
                                                />
                                            ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </ChartCard>
                    </div>
                )}

                {/* Cancellation Detail Table */}
                {cancellationData.length > 0 ? (
                    <DataTable
                        data={cancellationData}
                        columns={cancelColumns}
                        title="Cancellation Details by Category"
                        icon={XCircle}
                        exportFilename="category_cancellations"
                        defaultPageSize={10}
                    />
                ) : (
                    <div style={{
                        padding: '2rem',
                        textAlign: 'center',
                        background: 'var(--color-card)',
                        border: '1px solid var(--color-border)',
                        borderRadius: 'var(--radius-lg)',
                        color: 'var(--color-success)',
                        fontWeight: 600,
                    }}>
                        ✅ No cancellations in the selected period.
                    </div>
                )}
            </section>

            {/* ════════════════════════════════════════════════════
                REVENUE & VOLUME ANALYSIS (active orders only)
            ════════════════════════════════════════════════════ */}
            <section className={styles.section} style={{ marginTop: 'var(--spacing-5)' }}>
                <h2 className={styles.sectionTitle}>
                    <BarChart3 size={20} className={styles.sectionIcon} />
                    Revenue &amp; Volume Analysis
                    <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 400, color: 'var(--color-text-secondary)', marginLeft: 'var(--spacing-2)' }}>
                        (from active / delivered orders only)
                    </span>
                </h2>

                <div className={styles.chartsGrid}>
                    {/* Revenue by Category - Horizontal bar */}
                    <ChartCard title="Revenue by Category" icon={IndianRupee} height={300}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={categoryData.map(c => ({ name: c.name, Revenue: Math.round(c.revenue), fill: c.color }))}
                                layout="vertical"
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                                <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={v => `₹${(v / 1000).toFixed(0)}K`} />
                                <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={80} />
                                <Tooltip
                                    formatter={v => formatCurrency(v)}
                                    contentStyle={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 8 }}
                                />
                                <Bar dataKey="Revenue" radius={[0, 4, 4, 0]}>
                                    {categoryData.map((entry, i) => (<Cell key={i} fill={entry.color} />))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>

                    {/* Revenue Share Pie */}
                    <ChartCard title="Revenue Share %" icon={PieIcon} height={300}>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={pieData}
                                    dataKey="value"
                                    nameKey="name"
                                    innerRadius={55}
                                    outerRadius={100}
                                    paddingAngle={2}
                                    label={({ name, percent }) => `${name.split(' ')[0]} ${(percent * 100).toFixed(0)}%`}
                                    labelLine={false}
                                >
                                    {pieData.map((entry, i) => (<Cell key={i} fill={entry.color} />))}
                                </Pie>
                                <Tooltip formatter={v => formatCurrency(v)} />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </div>

                {/* Multi-line category revenue trend */}
                <ChartCard title="Category Revenue Trend (30 Days)" icon={TrendingUp} height={300}>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={categoryTrendData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                            <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                            <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `₹${(v / 1000).toFixed(0)}K`} />
                            <Tooltip
                                formatter={v => formatCurrency(v)}
                                contentStyle={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 8 }}
                            />
                            <Legend />
                            {categoryData.slice(0, 6).map(cat => (
                                <Line key={cat.id} type="monotone" dataKey={cat.name} stroke={cat.color} strokeWidth={2} dot={false} />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                </ChartCard>
            </section>

            {/* Per Kg Analysis */}
            {perKgData.length > 0 && (
                <section className={styles.section}>
                    <h2 className={styles.sectionTitle}>
                        <Scale size={20} className={styles.sectionIcon} />
                        Avg Rate Per Kg (from real order weights)
                    </h2>
                    <ChartCard title="Avg Rate/Kg by Category" icon={Weight} height={260}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={perKgData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                                <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `₹${v}`} />
                                <Tooltip
                                    formatter={v => `₹${v}/kg`}
                                    contentStyle={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 8 }}
                                />
                                <Bar dataKey="Avg Rate/Kg" radius={[4, 4, 0, 0]}>
                                    {perKgData.map((entry, i) => (<Cell key={i} fill={entry.color} />))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </section>
            )}

            {/* Category Detail Table */}
            <section className={styles.section}>
                <DataTable
                    data={categoryData}
                    columns={tableColumns}
                    title="Category Details (Active Orders)"
                    icon={Layers}
                    exportFilename="category_performance"
                    defaultPageSize={10}
                />
            </section>
        </div>
    );
}

export default CategoryPerformance;
